import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { collection, doc, onSnapshot, query, updateDoc, where } from 'firebase/firestore';
import { CalendarDays, Car, CheckCircle2, LogOut, Pencil, Plus, Save, ShieldCheck, Trash2 } from 'lucide-react';
import { db, firebaseReady } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import Reveal from '../components/Reveal';
import { Field, Modal } from './admin/adminUi';
import VehicleClassPicker from '../components/VehicleClassPicker';
import { VEHICLE_CLASSES, type Booking, type BookingStatus, type UserVehicle } from '../lib/types';
import { formatDate, money } from '../lib/format';

const statusStyles: Record<BookingStatus, string> = {
  pending: 'border-amber-400/40 bg-amber-400/10 text-amber-300',
  confirmed: 'border-sky-400/40 bg-sky-400/10 text-sky-300',
  in_progress: 'border-violet-400/40 bg-violet-400/10 text-violet-300',
  completed: 'border-emerald-400/40 bg-emerald-400/10 text-emerald-300',
  cancelled: 'border-zinc-500/40 bg-zinc-500/10 text-zinc-400',
};

const emptyVehicle: Omit<UserVehicle, 'id'> = { year: '', make: '', model: '', vehicleClass: 'car', color: '' };

export default function Account() {
  const { user, profile, loading, isStaff, signOut, saveProfile } = useAuth();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [contact, setContact] = useState({ name: '', phone: '' });
  const [savedMsg, setSavedMsg] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Partial<UserVehicle> | null>(null);

  useEffect(() => {
    if (profile) setContact({ name: profile.name, phone: profile.phone });
  }, [profile?.name, profile?.phone]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!firebaseReady || !user) return;
    return onSnapshot(
      query(collection(db(), 'bookings'), where('uid', '==', user.uid)),
      (snap) => setBookings(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Booking)),
      () => setBookings([]),
    );
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!firebaseReady) return <Navigate to="/" replace />;
  if (!loading && !user) return <Navigate to="/login?redirect=/account" replace />;
  if (loading || !user) {
    return <div className="container-x py-24 text-center text-sm text-zinc-500">Loading your account…</div>;
  }

  const sorted = [...bookings].sort((a, b) => b.date.localeCompare(a.date));
  const vehicles = profile?.vehicles ?? [];

  const saveContact = async () => {
    await saveProfile({ name: contact.name, phone: contact.phone });
    setSavedMsg(true);
    setTimeout(() => setSavedMsg(false), 2500);
  };

  const saveVehicle = async () => {
    if (!editingVehicle?.make || !editingVehicle?.model) return;
    const v: UserVehicle = {
      ...emptyVehicle,
      ...editingVehicle,
      id: editingVehicle.id ?? `v-${Date.now()}`,
    } as UserVehicle;
    const next = editingVehicle.id
      ? vehicles.map((x) => (x.id === v.id ? v : x))
      : [...vehicles, v];
    await saveProfile({ vehicles: next });
    setEditingVehicle(null);
  };

  const removeVehicle = async (v: UserVehicle) => {
    if (confirm(`Remove your ${v.year} ${v.make} ${v.model} from the garage?`)) {
      await saveProfile({ vehicles: vehicles.filter((x) => x.id !== v.id) });
    }
  };

  const cancelBooking = async (b: Booking) => {
    if (confirm(`Cancel your ${b.packageName} on ${formatDate(b.date)}?`)) {
      await updateDoc(doc(db(), 'bookings', b.id), { status: 'cancelled' });
    }
  };

  return (
    <div className="container-x py-12 sm:py-16">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="eyebrow">My Account</span>
            <h1 className="heading-lg mt-2">
              Hey{profile?.name ? `, ${profile.name.split(' ')[0]}` : ''} 👋
            </h1>
          </div>
          <div className="flex gap-3">
            {isStaff && (
              <Link to="/admin" className="btn-ghost !px-4 !py-2 text-sm">
                <ShieldCheck size={15} /> Staff Panel
              </Link>
            )}
            <button className="btn-ghost !px-4 !py-2 text-sm" onClick={() => signOut()}>
              <LogOut size={15} /> Sign Out
            </button>
          </div>
        </div>
      </Reveal>

      <div className="mt-8 grid gap-6 lg:grid-cols-[360px_1fr]">
        <div className="space-y-6">
          {/* Profile */}
          <Reveal>
            <section className="card p-6">
              <h2 className="font-display font-bold text-white">Profile</h2>
              <div className="mt-4 space-y-4">
                <Field label="Name">
                  <input className="input" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
                </Field>
                <Field label="Phone">
                  <input className="input" type="tel" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="(613) 555-0123" />
                </Field>
                <Field label="Email">
                  <input className="input opacity-60" value={user.email ?? ''} disabled />
                </Field>
                <div className="flex items-center gap-3">
                  <button className="btn-primary !px-4 !py-2 text-sm" onClick={saveContact}>
                    <Save size={14} /> Save
                  </button>
                  {savedMsg && <span className="flex items-center gap-1 text-sm text-emerald-400"><CheckCircle2 size={14} /> Saved</span>}
                </div>
              </div>
            </section>
          </Reveal>

          {/* Garage */}
          <Reveal delay={100}>
            <section className="card p-6">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-display font-bold text-white">
                  <Car size={17} className="text-accent-400" /> My Garage
                </h2>
                <button className="btn-ghost !px-3 !py-1.5 text-xs" onClick={() => setEditingVehicle({ ...emptyVehicle })}>
                  <Plus size={13} /> Add Vehicle
                </button>
              </div>
              {vehicles.length === 0 ? (
                <p className="mt-4 text-sm text-zinc-500">
                  No vehicles saved yet — add one and booking gets twice as fast.
                </p>
              ) : (
                <ul className="mt-4 space-y-2.5">
                  {vehicles.map((v) => (
                    <li key={v.id} className="flex items-center justify-between gap-3 rounded-xl border border-white/[0.08] bg-white/[0.02] p-3.5">
                      <div>
                        <p className="font-medium text-white">{v.year} {v.make} {v.model}</p>
                        <p className="text-xs text-zinc-500">
                          {VEHICLE_CLASSES.find((c) => c.id === v.vehicleClass)?.label}
                          {v.color ? ` · ${v.color}` : ''}
                        </p>
                      </div>
                      <div className="flex gap-1.5">
                        <button className="glass rounded-lg p-2 text-zinc-300 hover:text-accent-300" onClick={() => setEditingVehicle(v)} aria-label="Edit vehicle">
                          <Pencil size={14} />
                        </button>
                        <button className="glass rounded-lg p-2 text-zinc-300 hover:text-red-400" onClick={() => removeVehicle(v)} aria-label="Remove vehicle">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </Reveal>
        </div>

        {/* Bookings */}
        <Reveal delay={150}>
          <section className="card p-6">
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-display font-bold text-white">
                <CalendarDays size={17} className="text-accent-400" /> My Bookings
              </h2>
              <Link to="/booking" className="btn-primary !px-4 !py-2 text-sm">Book a Detail</Link>
            </div>
            {sorted.length === 0 ? (
              <p className="mt-6 text-sm text-zinc-500">
                No bookings yet. Bookings made while signed in show up here automatically.
              </p>
            ) : (
              <ul className="mt-5 space-y-3">
                {sorted.map((b) => (
                  <li key={b.id} className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-display font-semibold text-white">{b.packageName}</p>
                        <p className="mt-0.5 text-xs text-zinc-500">
                          {formatDate(b.date)} at {b.timeSlot} · {b.vehicle.year} {b.vehicle.make} {b.vehicle.model}
                          {b.serviceLocation === 'mobile' ? ' · mobile service' : ' · shop drop-off'}
                        </p>
                      </div>
                      <span className="font-display font-bold text-white">{money(b.estimatedTotal)}</span>
                      <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${statusStyles[b.status] ?? statusStyles.pending}`}>
                        {b.status.replace('_', ' ')}
                      </span>
                    </div>
                    {(b.status === 'pending' || b.status === 'confirmed') && (
                      <button
                        className="mt-3 text-xs font-semibold text-red-400 hover:text-red-300"
                        onClick={() => cancelBooking(b)}
                      >
                        Cancel this booking
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </Reveal>
      </div>

      {editingVehicle && (
        <Modal title={editingVehicle.id ? 'Edit Vehicle' : 'Add a Vehicle'} onClose={() => setEditingVehicle(null)}>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Year">
              <input className="input" value={editingVehicle.year ?? ''} onChange={(e) => setEditingVehicle({ ...editingVehicle, year: e.target.value })} placeholder="2022" />
            </Field>
            <Field label="Make">
              <input className="input" value={editingVehicle.make ?? ''} onChange={(e) => setEditingVehicle({ ...editingVehicle, make: e.target.value })} placeholder="Honda" />
            </Field>
            <Field label="Model">
              <input className="input" value={editingVehicle.model ?? ''} onChange={(e) => setEditingVehicle({ ...editingVehicle, model: e.target.value })} placeholder="Civic" />
            </Field>
            <Field label="Colour">
              <input className="input" value={editingVehicle.color ?? ''} onChange={(e) => setEditingVehicle({ ...editingVehicle, color: e.target.value })} placeholder="Black" />
            </Field>
            <div className="sm:col-span-3">
              <label className="label">Size class</label>
              <VehicleClassPicker
                value={editingVehicle.vehicleClass ?? 'car'}
                onChange={(v) => setEditingVehicle({ ...editingVehicle, vehicleClass: v })}
              />
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button className="btn-ghost !py-2.5 text-sm" onClick={() => setEditingVehicle(null)}>Cancel</button>
            <button className="btn-primary !py-2.5 text-sm" onClick={saveVehicle} disabled={!editingVehicle.make || !editingVehicle.model}>
              Save Vehicle
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
